import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertCircle,
  Mail,
  FileText,
  Send,
  RotateCcw,
  CheckCircle,
  Copy,
} from 'lucide-react';
import { fetchEmailTemplates, sendTestEmail } from '@/api/admin';
import { toast } from 'sonner';

export function AdminEmailToolsPage() {
  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['admin-email-templates'],
    queryFn: fetchEmailTemplates,
  });

  const [destination, setDestination] = useState('');
  const [subject, setSubject] = useState('');
  const [template, setTemplate] = useState('');
  const [jsonContext, setJsonContext] = useState('{}');
  const [message, setMessage] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [lastSent, setLastSent] = useState<{ to: string; template?: string; time: Date } | null>(null);

  const validateJson = (value: string) => {
    if (!value.trim()) {
      setJsonError(null);
      return true;
    }
    try {
      JSON.parse(value);
      setJsonError(null);
      return true;
    } catch {
      setJsonError('Invalid JSON format');
      return false;
    }
  };

  const validateEmail = (value: string) => {
    if (!value.trim()) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Invalid email format');
      return false;
    }
    setEmailError(null);
    return true;
  };

  const sendMutation = useMutation({
    mutationFn: () => {
      if (!validateEmail(destination)) {
        throw new Error('Invalid email address');
      }
      if (!validateJson(jsonContext)) {
        throw new Error('Invalid JSON context');
      }
      return sendTestEmail({
        destination_email: destination,
        subject,
        selected_template: template || undefined,
        json_context: jsonContext ? JSON.parse(jsonContext) : undefined,
        message,
      });
    },
    onSuccess: () => {
      toast.success('Test email sent/queued');
      setLastSent({ to: destination, template: template || undefined, time: new Date() });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to send'),
  });

  const resetForm = () => {
    setDestination('');
    setSubject('');
    setTemplate('');
    setJsonContext('{}');
    setMessage('');
    setJsonError(null);
    setEmailError(null);
  };

  const selectTemplate = (t: string) => {
    setTemplate(t);
    toast.success(`Selected template: ${t}`);
  };

  const copyTemplateName = (t: string) => {
    navigator.clipboard.writeText(t);
    toast.success('Template name copied');
  };

  const canSend = destination && !jsonError && !emailError && (template || message || subject);

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Email Tools</h1>
          <p className="text-sm text-muted-foreground">Send test emails and render templates</p>
        </div>
        <Button variant="outline" onClick={resetForm}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Templates</p>
                <p className="text-2xl font-semibold">{templates?.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Mail className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Selected</p>
                <p className="text-lg font-semibold truncate max-w-[120px]">
                  {template || 'None'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Send className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-lg font-semibold">
                  {sendMutation.isPending ? 'Sending...' : 'Ready'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Sent</p>
                <p className="text-sm font-medium truncate max-w-[120px]">
                  {lastSent ? lastSent.to : 'Never'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load templates.</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Templates Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Available Templates
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : templates?.length ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {templates.map((t) => (
                  <div
                    key={t}
                    className={`flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors ${
                      template === t ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                    onClick={() => selectTemplate(t)}
                  >
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{t}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {template === t && (
                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Selected</Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyTemplateName(t);
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No templates found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Send Test Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Test Email
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Destination Email */}
            <div className="space-y-2">
              <Label htmlFor="destination">Destination Email *</Label>
              <Input
                id="destination"
                type="email"
                placeholder="user@example.com"
                value={destination}
                onChange={(e) => {
                  setDestination(e.target.value);
                  if (e.target.value) validateEmail(e.target.value);
                }}
                onBlur={() => validateEmail(destination)}
                className={emailError ? 'border-red-500' : ''}
              />
              {emailError && (
                <p className="text-xs text-red-500">{emailError}</p>
              )}
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                placeholder="Email subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* Template */}
            <div className="space-y-2">
              <Label htmlFor="template">Template</Label>
              <Input
                id="template"
                placeholder="Select from list or type name"
                list="template-list"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
              />
              <datalist id="template-list">
                {templates?.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
              <p className="text-xs text-muted-foreground">
                Click a template on the left or type the name
              </p>
            </div>

            {/* JSON Context */}
            <div className="space-y-2">
              <Label htmlFor="json-context">JSON Context</Label>
              <Textarea
                id="json-context"
                placeholder='{"name": "Alice", "order_id": 123}'
                value={jsonContext}
                onChange={(e) => {
                  setJsonContext(e.target.value);
                  validateJson(e.target.value);
                }}
                rows={4}
                className={`font-mono text-xs ${jsonError ? 'border-red-500' : ''}`}
              />
              {jsonError ? (
                <p className="text-xs text-red-500">{jsonError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Variables to pass to the template
                </p>
              )}
            </div>

            {/* Fallback Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Fallback Message</Label>
              <Textarea
                id="message"
                placeholder="Plain text message (used if no template selected)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Used when no template is selected
              </p>
            </div>

            {/* Send Button */}
            <Button
              onClick={() => sendMutation.mutate()}
              disabled={sendMutation.isPending || !canSend}
              className="w-full"
            >
              <Send className="h-4 w-4 mr-2" />
              {sendMutation.isPending ? 'Sending...' : 'Send Test Email'}
            </Button>

            {/* Last Sent Info */}
            {lastSent && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-800 text-sm">
                  <CheckCircle className="h-4 w-4" />
                  <span className="font-medium">Last email sent</span>
                </div>
                <div className="mt-1 text-xs text-green-700">
                  <p>To: {lastSent.to}</p>
                  {lastSent.template && <p>Template: {lastSent.template}</p>}
                  <p>At: {lastSent.time.toLocaleString()}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
